/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import {
    object, shape, string, bool,
} from 'prop-types';
import ReactHTMLParser from 'react-html-parser';
import { useQuery } from '@apollo/client';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import mbpLogger from '@module-logger/mbp';
import { getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';
import GraphqlBreadcrumbs from '../../../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import GraphqlProductDetailsUI from '../GraphqlProductDetailsUI';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import Graphql404ErrorProductContainer from './Graphql404ErrorProductContainer';
import findContent from '../../../../../gql/queries/findContent';

const useStyles = makeStyles((theme) => ({
    container: {
        maxWidth: 1400,
        margin: '0 auto',
    },
    mainContainer: {
        display: 'flex',
        [theme.breakpoints.down(600)]: {
            display: 'block',
        },
    },
    rightBlock: {
        paddingLeft: 30,
        width: '61%',
        [theme.breakpoints.down(600)]: {
            width: '100%',
            padding: '0 15px',
        },
    },
    leftBlock: {
        width: '39%',
        [theme.breakpoints.down(600)]: {
            width: '100%',
        },
    },
    oopsContainer: {
        lineHeight: '1.2',
        '& h3': {
            margin: '0 0 5px 0',
        },
    },
    oopsMobileContainer: {
        lineHeight: '1.2',
        paddingBottom: 15,
        '& h3': {
            margin: '0 0 5px 0',
        },
    },
    image: {
        width: '100%',
    },
    productImage: {
        [theme.breakpoints.down(600)]: {
            textAlign: 'center',
        },
    },
    breadCrumbArray: {
        padding: '10px 0',
        [theme.breakpoints.down(600)]: {
            padding: '10px',
        },
    },
    oopsText: {
        color: theme.palette?.cms?.oopsTestColor || '#a94442',
        background: theme.palette?.cms?.oopsTextBackground || '#ebcccc',
        padding: '15px',
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        letterSpacing: '.03em',
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.004)',
        [theme.breakpoints.down(600)]: {
            fontWeight: 'normal',
        },
    },
    bestSellerContainer: {
        padding: '15px',
    },
    bestSellersText: {
        color: theme.palette.black,
        fontSize: '18px',
    },
    productName: {
        marginTop: '0px',
        [theme.breakpoints.down(600)]: {
            fontSize: 25,
            paddingLeft: 15,
        },
    },
}));

const ProductNotAvailablePage = ({
    data, brand, seoData, ssrDeviceType,
}) => {
    const classes = useStyles();

    const {
        image, name, longDescription, partNumber,
    } = data;

    const TEMPLATE_ERROR_PAGE = findContent({
        brand: brand?.domain,
        feed: 'findContentPage',
        contentType: 'template_error_page',
        url: '/404-page',
    });

    let errorText = "We're sorry, this product is not available. Please shop our top trending products that customers like you have chosen to connect, express and celebrate.";

    const { data: templateCopyData, error, loading } = useQuery(TEMPLATE_ERROR_PAGE);

    if (loading) {
        return null;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: TEMPLATE_ERROR_PAGE,
            component: 'ProductNotAvailablePage.js',
            message: 'Error loading data from Graphql',
            error,
        });
    }

    const outOfStockError = templateCopyData?.findContentPage?.content?.entries?.[0]?.out_of_stock_copy;

    if (outOfStockError) {
        errorText = outOfStockError;
    }

    const breadCrumb = [{
        title: 'Home',
        href: '/',
    },
    {
        title: name,
        href: '',
    }];

    const imageSource = `${image?.path + image?.name}c.jpg`;

    return (
        <Grid container className={classes.container}>
            {seoData?.seo_searchable && <GraphqlSeoHeadTags seoData={seoData} />}
            {name && <Helmet><title>{name}</title></Helmet>}
            <Grid item md={12} xs={12} className={classes.breadCrumbArray}>
                <div className={classes.breadCrumb}>
                    <GraphqlBreadcrumbs breadCrumbArray={breadCrumb} />
                </div>
            </Grid>
            <Grid item md={12} xs={12} className={classes.mainContainer}>
                <div className={classes.leftBlock}>
                    {ssrDeviceType === 'mobile' && (
                        <div className={classes.oopsMobileContainer}>
                            <h1 className={classes.productName}>{ReactHTMLParser(name)}</h1>
                            <div className={classes.oopsText}>{errorText}</div>
                        </div>
                    )}
                    <div className={classes.productImage}>
                        <ResponsiveImage
                            className={classes.image}
                            path={imageSource}
                            alt={name}
                            params={{
                                desktop: 'width=545&height=597',
                                tablet: 'width=406&height=445quality=100',
                            }}
                        />
                    </div>
                    <GraphqlProductDetailsUI longDescription={longDescription} partNumber={partNumber} />
                </div>
                <div className={classes.rightBlock}>
                    {(ssrDeviceType === 'desktop' || ssrDeviceType === 'tablet') && (
                        <div className={classes.oopsContainer}>
                            <h1 className={classes.productName}>{ReactHTMLParser(name)}</h1>
                            <div className={classes.oopsText}><strong>{errorText}</strong></div>
                        </div>
                    )}
                    <div className={classes.bestSellerContainer}>
                        <span className={classes.bestSellersText}>
                            <strong>Trending Products</strong>
                        </span>
                    </div>
                    <Graphql404ErrorProductContainer brand={brand} eventSubType="oos" productNotAvaliable />
                </div>
            </Grid>
        </Grid>
    );
};

ProductNotAvailablePage.propTypes = {
    brand: object.isRequired,
    data: shape({
        name: string.isRequired,
        longDescription: string.isRequired,
        image: shape({
            path: string.isRequired,
            name: string.isRequired,
        }),
    }).isRequired,
    seoData: shape({
        keywords: string,
        meta_description: string,
        page_title: string,
        seo_searchable: bool,
    }),
    ssrDeviceType: string.isRequired,
};

ProductNotAvailablePage.defaultProps = {
    seoData: {},
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});
const enhance = compose(
    connect(mapStateToProps, null),
);

export default enhance(ProductNotAvailablePage);
